import type { Proxy } from '#~/agent/types.js';

/** Read-only, allowlisted state for HIL failures; never serialize a proxy or URI.
 * Per preserve-safe-diagnostic-error-codes (ai.knowledge/global).
 */
export function readDeviceDiagnostics(
  proxy: Pick<Proxy, 'state' | 'id' | 'roomId' | 'embedded'> | undefined,
  deviceId: string
) {
  const status = proxy?.state.status?.device?.[deviceId];
  const movement = status?.capabilities?.movement;
  const settings = status?.deviceSettings;
  const finite = (value: number | undefined) =>
    typeof value === 'number' && Number.isFinite(value) ? value : null;
  const ptz = (value?: {
    pan?: number | undefined;
    tilt?: number | undefined;
    zoom?: number | undefined;
  }) => ({
    pan: finite(value?.pan),
    tilt: finite(value?.tilt),
    zoom: finite(value?.zoom)
  });
  return {
    proxyPresent: !!proxy,
    owner: proxy ? { agentId: proxy.id, roomId: proxy.roomId, embedded: proxy.embedded } : null,
    devicePresent: !!status,
    video: {
      connectionStatus: finite(status?.video?.connectionStatus?.status),
      streamState: finite(status?.video?.status?.state),
      codec: /^video\/(h264|h265|vp8|vp9|av1)$/i.test(status?.video?.status?.codec ?? '')
        ? status?.video?.status?.codec
        : null
    },
    position: ptz(status?.movement?.position),
    intent: ptz(proxy?.state.control?.device?.[deviceId]?.movement?.quickShots),
    capabilities: {
      pan: movement?.vectors?.pan?.enabled ?? null,
      tilt: movement?.vectors?.tilt?.enabled ?? null,
      zoom: movement?.vectors?.zoom?.enabled ?? null,
      home: movement?.canMoveHome ?? null
    },
    calibrationState: status?.calibration?.status?.state ?? null,
    settingsPresent: !!settings,
    settingsCount: Object.keys(settings?.settings ?? {}).length,
    commandCount: Object.keys(settings?.commandDefinitions ?? {}).length,
    // Section type and cardinality distinguish absent metadata from UI filtering.
    sections: Object.values(settings?.interfaceSections ?? {}).map((section) => ({
      type: section?.type ?? null,
      itemCount: Object.keys(section?.items ?? {}).length
    }))
  };
}
